import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from '../../utils/api';

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function BackupCard({ icon, title, description, buttonLabel, onAction, loading, accent }) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: `1.5px solid ${accent}22`, borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>{icon}</div>
      <div>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 6px' }}>{title}</h3>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>{description}</p>
      </div>
      <button
        onClick={onAction}
        disabled={loading}
        style={{ marginTop: 'auto', padding: '10px 20px', borderRadius: 10, border: 'none', background: loading ? 'var(--n-200)' : accent, color: loading ? '#94a3b8' : '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'default' : 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
      >
        {loading ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Working…</> : buttonLabel}
      </button>
    </div>
  );
}

function BackupsTab() {
  const [backups, setBackups]         = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);
  const [restoring, setRestoring]     = useState(false);
  const [message, setMessage]         = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validating, setValidating]   = useState(null); // filename being validated
  const [validateResult, setValidateResult] = useState(null);
  const fileRef = useRef();

  function notify(type, text) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 6000);
  }

  async function loadBackups() {
    const data = await apiFetch('/maintenance/backups').catch(() => []);
    setBackups(data || []);
  }

  useEffect(() => { loadBackups(); }, []);

  async function handleDataBackup() {
    setLoadingData(true);
    try {
      const res = await apiFetch('/maintenance/backup/data', { method: 'POST' });
      notify('success', `Data backup created: ${res.name} (${fmtSize(res.size)})`);
      loadBackups();
    } catch (e) { notify('error', e.message); }
    setLoadingData(false);
  }

  async function handleFullBackup() {
    setLoadingFull(true);
    try {
      const res = await apiFetch('/maintenance/backup/full', { method: 'POST' });
      notify('success', `Full backup created: ${res.name} (${fmtSize(res.size)})`);
      loadBackups();
    } catch (e) { notify('error', e.message); }
    setLoadingFull(false);
  }

  async function handleDelete(name) {
    if (!window.confirm(`Delete backup "${name}"?`)) return;
    await apiFetch(`/maintenance/backup/${encodeURIComponent(name)}`, { method: 'DELETE' }).catch(() => {});
    loadBackups();
  }

  async function handleValidate(name) {
    setValidating(name);
    setValidateResult(null);
    try {
      const isFull = name.endsWith('.tar.gz') || name.endsWith('.tgz');
      const endpoint = isFull
        ? `/maintenance/backup/validate-full/${encodeURIComponent(name)}`
        : `/maintenance/backup/validate/${encodeURIComponent(name)}`;
      const result = await apiFetch(endpoint, { method: 'POST' });
      setValidateResult({ ...result, _type: isFull ? 'full' : 'data' });
    } catch (e) {
      setValidateResult({ ok: false, integrity: false, detail: e.message, tables: [], _type: 'data' });
    }
    setValidating(null);
  }

  function handleDownload(name) {
    const token = sessionStorage.getItem('kgreen_token');
    const url = `/api/maintenance/download/${encodeURIComponent(name)}`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', name);
    // Pass auth header via fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const burl = URL.createObjectURL(blob);
        a.href = burl;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(burl);
      })
      .catch(() => notify('error', 'Download failed'));
  }

  async function handleRestore() {
    if (!selectedFile) return;
    if (!window.confirm(`Restore database from "${selectedFile.name}"?\n\nThis will REPLACE all current data and restart the server.`)) return;
    setRestoring(true);
    try {
      const token = sessionStorage.getItem('kgreen_token');
      const formData = new FormData();
      formData.append('backup', selectedFile);
      const res = await fetch('/api/maintenance/restore', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Restore failed');
      notify('success', data.message || 'Restore complete. Please wait for server to restart.');
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) { notify('error', e.message); }
    setRestoring(false);
  }

  const dataBackups = backups.filter(b => b.name.startsWith('data_backup_'));
  const fullBackups = backups.filter(b => b.name.startsWith('full_backup_'));

  return (
    <div>
      {/* Toast */}
      {message && (
        <div style={{ marginBottom: 20, padding: '12px 18px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10, background: message.type === 'success' ? 'var(--action-bg)' : 'var(--red-50)', border: `1px solid ${message.type === 'success' ? 'var(--action-hover)' : '#fca5a5'}`, color: message.type === 'success' ? 'var(--status-resolved-text)' : '#b91c1c', fontSize: 13, fontWeight: 600 }}>
          <span style={{ fontSize: 18 }}>{message.type === 'success' ? '✅' : '❌'}</span>
          {message.text}
        </div>
      )}

      {/* Action cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 36 }}>
        <BackupCard
          icon="🗄️"
          accent="#0B6623"
          title="Data Backup"
          description="Creates a snapshot of the database only (.db file). Fast — use this for routine backups before making changes."
          buttonLabel="Take Data Backup"
          onAction={handleDataBackup}
          loading={loadingData}
        />
        <BackupCard
          icon="📦"
          accent="var(--blue-500)"
          title="Full Backup"
          description="Archives the entire application — code, configuration, and database — into a single .tar.gz file. Use before upgrades."
          buttonLabel="Take Full Backup"
          onAction={handleFullBackup}
          loading={loadingFull}
        />

        {/* Restore card */}
        <div style={{ background: 'var(--bg-surface)', border: '1.5px solid #f59e0b22', borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: '#fef3c718', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>🔄</div>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 6px' }}>Restore Data</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>Upload a previously downloaded .db backup file to restore the database. The server will restart automatically.</p>
          </div>
          <div style={{ border: '2px dashed var(--border-subtle)', borderRadius: 10, padding: '18px 14px', textAlign: 'center', background: selectedFile ? 'var(--amber-50)' : 'var(--bg-app)', cursor: 'pointer' }}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setSelectedFile(f); }}>
            <input ref={fileRef} type="file" accept=".db" style={{ display: 'none' }} onChange={e => setSelectedFile(e.target.files[0] || null)} />
            {selectedFile
              ? <><div style={{ fontSize: 22, marginBottom: 4 }}>📁</div><div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>{selectedFile.name}</div><div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmtSize(selectedFile.size)} · click to change</div></>
              : <><div style={{ fontSize: 22, marginBottom: 4 }}>⬆️</div><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>Click or drag a .db file here</div></>
            }
          </div>
          <button
            onClick={handleRestore}
            disabled={!selectedFile || restoring}
            style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: !selectedFile || restoring ? 'var(--n-200)' : '#f59e0b', color: !selectedFile || restoring ? '#94a3b8' : '#fff', fontSize: 13, fontWeight: 700, cursor: !selectedFile || restoring ? 'default' : 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {restoring ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Restoring…</> : '⚠️ Restore Database'}
          </button>
        </div>
      </div>

      {/* Backup list */}
      <div>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-main)', marginBottom: 16 }}>Available Backups</h2>

        {backups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', background: 'var(--bg-app)', borderRadius: 12, fontSize: 14 }}>
            No backups yet. Take a backup above to get started.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: 20 }}>
            {/* Data backups */}
            {dataBackups.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>🗄️ Data Backups</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dataBackups.map(b => (
                    <BackupRow key={b.name} b={b} onDownload={handleDownload} onDelete={handleDelete} onValidate={handleValidate} />
                  ))}
                </div>
              </div>
            )}
            {/* Full backups */}
            {fullBackups.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>📦 Full Backups</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {fullBackups.map(b => (
                    <BackupRow key={b.name} b={b} onDownload={handleDownload} onDelete={handleDelete} onValidate={handleValidate} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validating indicator */}
      {validating && (
        <div style={{ marginTop: 16, padding: '10px 16px', borderRadius: 8, background: 'var(--action-bg)', border: '1px solid #bbf7d0', color: '#0B6623', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: '#0B6623', borderTopColor: 'transparent' }} />
          Validating {validating}…
        </div>
      )}

      {/* Validate result modal */}
      {validateResult && <ValidateModal result={validateResult} onClose={() => setValidateResult(null)} />}
    </div>
  );
}

function BackupRow({ b, onDownload, onDelete, onValidate }) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmtDate(b.created_at)} · {fmtSize(b.size)}</div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button onClick={() => onValidate(b.name)} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #bbf7d0', background: 'var(--action-bg)', color: '#0B6623', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✓ Validate</button>
        <button onClick={() => onDownload(b.name)} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #bfdbfe', background: 'var(--blue-50)', color: 'var(--blue-700)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>⬇ Download</button>
        <button onClick={() => onDelete(b.name)} style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid #fca5a5', background: 'var(--red-50)', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✕</button>
      </div>
    </div>
  );
}

function ValidateModal({ result, onClose }) {
  if (!result) return null;
  const allOk = result.ok;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,.2)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Backup Validation Report</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
        </div>

        {/* Summary banner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, marginBottom: 20,
          background: allOk ? '#d1fae5' : '#fee2e2',
          border: `1px solid ${allOk ? '#6ee7b7' : '#fca5a5'}` }}>
          <span style={{ fontSize: 20 }}>{allOk ? '✅' : '❌'}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: allOk ? '#065f46' : '#b91c1c' }}>
              {allOk ? 'Backup is valid and restorable' : 'Backup has issues'}
            </div>
            <div style={{ fontSize: 12, color: allOk ? '#047857' : '#dc2626', marginTop: 2 }}>{result.detail}</div>
          </div>
        </div>

        {/* Data backup: integrity check row */}
        {result._type !== 'full' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, marginBottom: 14,
            background: result.integrity ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${result.integrity ? '#bbf7d0' : '#fecaca'}` }}>
            <span>{result.integrity ? '✅' : '❌'}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: result.integrity ? '#065f46' : '#b91c1c' }}>
              SQLite integrity check — {result.integrity ? 'PASSED' : 'FAILED'}
            </span>
          </div>
        )}

        {/* Full backup: archive + file checks + db check */}
        {result._type === 'full' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
              background: result.archiveOk ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${result.archiveOk ? '#bbf7d0' : '#fecaca'}` }}>
              <span>{result.archiveOk ? '✅' : '❌'}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: result.archiveOk ? '#065f46' : '#b91c1c' }}>
                Archive integrity — {result.archiveOk ? 'PASSED (not corrupt)' : 'FAILED (corrupt or unreadable)'}
              </span>
            </div>

            {result.checks && result.checks.length > 0 && (
              <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Expected Files & Folders</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {result.checks.map(c => (
                    <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12 }}>{c.ok ? '✅' : '❌'}</span>
                      <span style={{ fontSize: 13, fontFamily: 'monospace', color: c.ok ? 'var(--text-primary)' : '#dc2626' }}>{c.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.dbCheck && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
                background: result.dbCheck.ok ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${result.dbCheck.ok ? '#bbf7d0' : '#fecaca'}` }}>
                <span>{result.dbCheck.ok ? '✅' : '❌'}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: result.dbCheck.ok ? '#065f46' : '#b91c1c' }}>
                  Database inside archive — {result.dbCheck.detail}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Table row count comparison — data backups only */}
        {result._type !== 'full' && result.tables && result.tables.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Table Row Counts</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {result.tables.map(t => {
                const missing = !t.ok;
                const behind = t.ok && t.diff !== null && t.diff < 0;
                const ahead  = t.ok && t.diff !== null && t.diff > 0;
                const exact  = t.ok && t.diff === 0;
                return (
                  <div key={t.table} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8,
                    background: missing ? '#fef2f2' : 'var(--bg-app)',
                    border: `1px solid ${missing ? '#fecaca' : 'var(--border-subtle)'}` }}>
                    <span style={{ fontSize: 12 }}>{missing ? '❌' : '✅'}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, flex: 1, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{t.table}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {t.backup !== null ? t.backup.toLocaleString('en-IN') : '—'} rows
                    </span>
                    {exact && <span style={{ fontSize: 11, color: '#0B6623', fontWeight: 700 }}>up to date</span>}
                    {behind && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#0369a1', background: '#dbeafe', padding: '2px 8px', borderRadius: 99 }}>
                        {Math.abs(t.diff)} newer in live
                      </span>
                    )}
                    {ahead && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 99 }}>
                        +{t.diff} only in backup
                      </span>
                    )}
                    {missing && <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>table missing</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-app)', borderRadius: 8, padding: '8px 12px' }}>
              ℹ️ Row count differences are normal — they just mean the backup is older than the current data.
            </div>
          </div>
        )}

        <div style={{ marginTop: 20, textAlign: 'right' }}>
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Privileges master list ─────────────────────────────────────────────────────
const PRIVILEGE_GROUPS = [
  {
    group: 'Tickets',
    items: [
      { key: 'tickets.view',      label: 'View Tickets' },
      { key: 'tickets.create',    label: 'Create Tickets' },
      { key: 'tickets.edit',      label: 'Edit / Update Tickets' },
      { key: 'tickets.close',     label: 'Close Tickets' },
      { key: 'tickets.reassign',  label: 'Reassign Tickets' },
      { key: 'tickets.delete',    label: 'Delete Tickets' },
    ],
  },
  {
    group: 'Customers',
    items: [
      { key: 'customers.view',   label: 'View Customers' },
      { key: 'customers.create', label: 'Create Customers' },
      { key: 'customers.edit',   label: 'Edit Customers' },
      { key: 'customers.delete', label: 'Delete Customers' },
    ],
  },
  {
    group: 'Knowledge & FAQ',
    items: [
      { key: 'knowledge.view',   label: 'View Knowledge Base & FAQ' },
      { key: 'knowledge.edit',   label: 'Edit Knowledge Base & FAQ' },
      { key: 'faq.submit',       label: 'Submit FAQ for Approval' },
      { key: 'faq.approve',      label: 'Approve / Reject FAQ (Admin only)' },
    ],
  },
  {
    group: 'Dashboards & Calendar',
    items: [
      { key: 'dashboard.view',  label: 'View Dashboard' },
      { key: 'empdash.view',    label: 'View Employee Dashboard' },
      { key: 'agentdash.view',  label: 'View Agent Ticket Dashboard' },
      { key: 'calendar.view',   label: 'View Ticket Calendar' },
    ],
  },
  {
    group: 'Reports & Analytics',
    items: [
      { key: 'reports.view',    label: 'View Reports' },
      { key: 'analytics.view',  label: 'View Analytics' },
    ],
  },
  {
    group: 'Users & Roles',
    items: [
      { key: 'users.view',    label: 'View Users' },
      { key: 'users.invite',  label: 'Invite / Create Users' },
      { key: 'users.edit',    label: 'Edit Users' },
      { key: 'roles.manage',  label: 'Manage Roles & Privileges' },
    ],
  },
  {
    group: 'Products & SLA',
    items: [
      { key: 'products.view',  label: 'View Products' },
      { key: 'products.edit',  label: 'Edit Products' },
      { key: 'sla.view',       label: 'View SLA Policies' },
      { key: 'sla.edit',       label: 'Edit SLA Policies' },
      { key: 'sla.stop',       label: 'Stop / Resume SLA Clock' },
    ],
  },
  {
    group: 'Projects',
    items: [
      { key: 'projects.view',      label: 'View Projects' },
      { key: 'projects.create',    label: 'Create Projects' },
      { key: 'projects.edit',      label: 'Edit Projects & Tasks' },
      { key: 'projects.templates', label: 'Manage Project Templates' },
      { key: 'projects.delete',    label: 'Delete Projects' },
    ],
  },
  {
    group: 'Live Chat',
    items: [
      { key: 'chat.handle',   label: 'Handle Live Chat' },
    ],
  },
  {
    group: 'Workforce',
    items: [
      { key: 'oof.view',   label: 'View OOF (Out of Office)' },
      { key: 'oof.manage', label: 'Set / Clear Agent OOF' },
    ],
  },
  {
    group: "Today's Plan",
    items: [
      { key: 'plan.own',    label: 'Manage Own Daily Plan' },
      { key: 'plan.team',   label: 'View Team Plans' },
    ],
  },
  {
    group: 'System (Super Admin)',
    items: [
      { key: 'maintenance.access',  label: 'Access Maintenance' },
      { key: 'backups.manage',      label: 'Backups & Restore' },
      { key: 'audit.view',          label: 'View Audit Trail' },
      { key: 'settings.manage',     label: 'Manage App Settings' },
      { key: 'health.view',         label: 'Run Health Check' },
      { key: 'roles.manage_all',    label: 'Manage All Roles (incl. system)' },
    ],
  },
];

const ROLE_COLORS = ['#0B6623','var(--blue-700)','#7c3aed','#ea580c','#be185d','#0369a1','#b45309','#0f766e'];

// ── Roles & Privileges Tab ─────────────────────────────────────────────────────
function RolesTab() {
  const [systemRoles, setSystemRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState(null); // role object currently being edited
  const [activePanel, setActivePanel] = useState('privileges'); // 'privileges' | 'users'
  const [allUsers, setAllUsers]       = useState([]);
  const [roleUsers, setRoleUsers]     = useState([]);
  const [addUserId, setAddUserId]     = useState('');
  const [addUserSearch, setAddUserSearch] = useState('');
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [privs, setPrivs]             = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm]         = useState({ name: '', description: '', color: ROLE_COLORS[0] });
  const [formError, setFormError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [data, users] = await Promise.all([
      apiFetch('/roles').catch(() => ({ system: [], custom: [] })),
      apiFetch('/roles/users/all').catch(() => []),
    ]);
    setSystemRoles(data.system || []);
    setCustomRoles(data.custom || []);
    setAllUsers(users || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function selectRole(role) {
    setSelected(role);
    setPrivs(role.privileges || []);
    setActivePanel('privileges');
    setRoleUsers([]);
    setAddUserId(''); setAddUserSearch('');
    if (!role.is_system) {
      const users = await apiFetch(`/roles/${role.id}/users`).catch(() => []);
      setRoleUsers(users || []);
    }
  }

  async function savePrivileges() {
    if (!selected) return;
    setSaving(true);
    await apiFetch(`/roles/${selected.id}/privileges`, { method: 'PUT', body: { privileges: privs } });
    setSaving(false);
    setCustomRoles(prev => prev.map(r => r.id === selected.id ? { ...r, privileges: privs } : r));
    setSystemRoles(prev => prev.map(r => r.id === selected.id ? { ...r, privileges: privs } : r));
    setSelected(s => ({ ...s, privileges: privs }));
  }

  function togglePriv(key) {
    setPrivs(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  }

  function toggleGroup(group) {
    const keys = group.items.map(i => i.key);
    const allOn = keys.every(k => privs.includes(k));
    setPrivs(p => allOn ? p.filter(k => !keys.includes(k)) : [...new Set([...p, ...keys])]);
  }

  async function addUser() {
    if (!addUserId || !selected) return;
    await apiFetch(`/roles/${selected.id}/users`, { method: 'POST', body: { user_id: addUserId } });
    const users = await apiFetch(`/roles/${selected.id}/users`).catch(() => []);
    setRoleUsers(users || []);
    setAddUserId(''); setAddUserSearch(''); setShowUserSuggestions(false);
    setCustomRoles(prev => prev.map(r => r.id === selected.id ? { ...r, user_count: (r.user_count || 0) + 1 } : r));
  }

  async function removeUser(userId) {
    await apiFetch(`/roles/${selected.id}/users/${userId}`, { method: 'DELETE' });
    setRoleUsers(prev => prev.filter(u => u.id !== userId));
    setCustomRoles(prev => prev.map(r => r.id === selected.id ? { ...r, user_count: Math.max(0, (r.user_count || 1) - 1) } : r));
  }

  async function createRole(e) {
    e.preventDefault();
    if (!newForm.name.trim()) { setFormError('Role name is required'); return; }
    setFormError('');
    try {
      const role = await apiFetch('/roles', { method: 'POST', body: newForm });
      setCustomRoles(prev => [...prev, role]);
      setShowNewForm(false);
      setNewForm({ name: '', description: '', color: ROLE_COLORS[0] });
      selectRole(role);
    } catch (err) { setFormError(err.message); }
  }

  async function deleteRole(id) {
    if (!window.confirm('Delete this role? All user assignments will be removed.')) return;
    await apiFetch(`/roles/${id}`, { method: 'DELETE' });
    setCustomRoles(prev => prev.filter(r => r.id !== id));
    if (selected?.id === id) setSelected(null);
  }

  const availableToAdd = allUsers.filter(u =>
    !roleUsers.some(ru => ru.id === u.id) &&
    (!addUserSearch || u.name.toLowerCase().includes(addUserSearch.toLowerCase()) || u.email.toLowerCase().includes(addUserSearch.toLowerCase()))
  );

  const ROLE_COLOR_BG = { admin: '#f5f3ff', manager: '#fff7ed', agent: 'var(--action-bg)', customer: 'var(--blue-50)', customer_manager: '#fdf2f8' };
  const ROLE_COLOR_TEXT = { admin: '#7c3aed', manager: '#ea580c', agent: '#0B6623', customer: '#0369a1', customer_manager: '#be185d' };

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', minHeight: 500 }}>
      {/* Left: Role list */}
      <div style={{ width: 260, flexShrink: 0 }}>
        {/* System roles */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>System Roles</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20 }}>
          {systemRoles.map(r => (
            <div key={r.id} onClick={() => selectRole(r)}
              style={{ padding: '10px 14px', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                background: selected?.id === r.id ? (ROLE_COLOR_BG[r.name.toLowerCase().replace(' ','_')] || 'var(--action-bg)') : 'var(--bg-surface)',
                border: `1.5px solid ${selected?.id === r.id ? (r.color || '#0B6623') : 'var(--border-subtle)'}`,
                transition: 'all .12s' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)', flex: 1 }}>{r.name}</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>system</span>
            </div>
          ))}
        </div>

        {/* Custom roles */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .5 }}>Custom Roles</div>
          <button onClick={() => { setShowNewForm(v => !v); setFormError(''); }}
            style={{ fontSize: 11, fontWeight: 700, color: 'var(--status-resolved-text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {showNewForm ? '✕ Cancel' : '+ New'}
          </button>
        </div>

        {showNewForm && (
          <form onSubmit={createRole} style={{ background: 'var(--bg-app)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 14, marginBottom: 12 }}>
            {formError && <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>{formError}</div>}
            <input className="input" style={{ fontSize: 13, marginBottom: 8 }} placeholder="Role name *" value={newForm.name}
              onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} />
            <input className="input" style={{ fontSize: 13, marginBottom: 10 }} placeholder="Description (optional)" value={newForm.description}
              onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} />
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Colour</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ROLE_COLORS.map(c => (
                  <div key={c} onClick={() => setNewForm(f => ({ ...f, color: c }))}
                    style={{ width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer',
                      border: newForm.color === c ? '2.5px solid #1e293b' : '2px solid transparent' }} />
                ))}
              </div>
            </div>
            <button className="btn btn-primary btn-sm" style={{ width: '100%', background: newForm.color }}>Create Role</button>
          </form>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {loading ? <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '12px 0' }}>Loading…</div>
            : customRoles.length === 0
              ? <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', padding: '8px 0' }}>No custom roles yet.</div>
              : customRoles.map(r => (
                <div key={r.id} onClick={() => selectRole(r)}
                  style={{ padding: '10px 14px', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    background: selected?.id === r.id ? 'var(--action-bg)' : 'var(--bg-surface)',
                    border: `1.5px solid ${selected?.id === r.id ? r.color : 'var(--border-subtle)'}`,
                    transition: 'all .12s' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)', flex: 1 }}>{r.name}</span>
                  <span style={{ fontSize: 11, background: 'var(--n-100)', borderRadius: 6, padding: '1px 6px', color: 'var(--text-secondary)' }}>{r.user_count}</span>
                  <button onClick={e => { e.stopPropagation(); deleteRole(r.id); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer', padding: 0, lineHeight: 1 }}
                    title="Delete role">✕</button>
                </div>
              ))
          }
        </div>
      </div>

      {/* Right: Detail panel */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selected ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🔐</div>
            <p style={{ fontSize: 14 }}>Select a role on the left to view or edit its privileges.</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 24 }}>
            {/* Role header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <span style={{ width: 14, height: 14, borderRadius: '50%', background: selected.color || 'var(--text-secondary)', flexShrink: 0 }} />
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{selected.name}</h2>
                {selected.description && <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '2px 0 0' }}>{selected.description}</p>}
              </div>
              {selected.is_system && (
                <span style={{ marginLeft: 'auto', fontSize: 11, background: 'var(--n-100)', color: 'var(--text-secondary)', borderRadius: 6, padding: '3px 8px', fontWeight: 600 }}>
                  System Role — Read Only
                </span>
              )}
            </div>

            {/* Tabs: Privileges | Users */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 20 }}>
              {[{ id: 'privileges', label: '🔑 Privileges' }, ...(!selected.is_system ? [{ id: 'users', label: '👥 Users' }] : [])].map(t => (
                <button key={t.id} onClick={() => setActivePanel(t.id)}
                  style={{ padding: '8px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                    color: activePanel === t.id ? 'var(--status-resolved-text)' : 'var(--text-secondary)',
                    borderBottom: `3px solid ${activePanel === t.id ? 'var(--status-resolved-text)' : 'transparent'}`,
                    marginBottom: -2, transition: 'all .15s' }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Privileges panel */}
            {activePanel === 'privileges' && (
              <div>
                {selected.is_system && (
                  <div style={{ fontSize: 12.5, color: '#92400e', background: 'var(--amber-50)', border: '1px solid var(--amber-100)', borderRadius: 8, padding: '9px 12px', marginBottom: 14 }}>
                    {selected.name === 'Admin'
                      ? 'Admins always have full access — settings here are recorded but Admins are never restricted.'
                      : `Privileges you grant to ${selected.name} apply to every ${selected.name.toLowerCase()} user.`}
                  </div>
                )}
                {(
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 20 }}>
                      {PRIVILEGE_GROUPS.map(group => {
                        const allOn = group.items.every(i => privs.includes(i.key));
                        const someOn = group.items.some(i => privs.includes(i.key));
                        return (
                          <div key={group.group} style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden' }}>
                            <div onClick={() => toggleGroup(group)}
                              style={{ padding: '10px 14px', background: allOn ? 'var(--action-bg)' : someOn ? 'var(--amber-50)' : 'var(--bg-app)',
                                borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input type="checkbox" readOnly checked={allOn} ref={el => { if (el) el.indeterminate = someOn && !allOn; }}
                                style={{ cursor: 'pointer', width: 15, height: 15 }} />
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{group.group}</span>
                            </div>
                            <div style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {group.items.map(item => (
                                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>
                                  <input type="checkbox" checked={privs.includes(item.key)} onChange={() => togglePriv(item.key)}
                                    style={{ cursor: 'pointer', width: 14, height: 14 }} />
                                  {item.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button className="btn btn-primary" style={{ background: '#0B6623' }} onClick={savePrivileges} disabled={saving}>
                      {saving ? 'Saving…' : '💾 Save Privileges'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Users panel */}
            {activePanel === 'users' && !selected.is_system && (
              <div>
                {/* Add user */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 20, position: 'relative' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input className="input" style={{ fontSize: 13 }} placeholder="Search user by name or email…"
                      value={addUserSearch} autoComplete="off"
                      onChange={e => { setAddUserSearch(e.target.value); setAddUserId(''); setShowUserSuggestions(true); }}
                      onFocus={() => setShowUserSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowUserSuggestions(false), 150)} />
                    {showUserSuggestions && addUserSearch && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.1)', zIndex: 100, maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
                        {availableToAdd.length === 0
                          ? <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>No users found</div>
                          : availableToAdd.map(u => (
                            <div key={u.id}
                              onPointerDown={e => { e.preventDefault(); setAddUserId(u.id); setAddUserSearch(u.name); setShowUserSuggestions(false); }}
                              style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 8, alignItems: 'center' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--action-bg)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-surface)'}>
                              <span style={{ fontWeight: 600 }}>{u.name}</span>
                              <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{u.email}</span>
                              <span style={{ marginLeft: 'auto', fontSize: 11, background: 'var(--n-100)', borderRadius: 5, padding: '1px 6px', color: 'var(--text-secondary)' }}>{u.role}</span>
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                  <button className="btn btn-primary" style={{ background: '#0B6623', whiteSpace: 'nowrap' }} disabled={!addUserId} onClick={addUser}>
                    + Assign
                  </button>
                </div>

                {/* Assigned users list */}
                {roleUsers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-secondary)', background: 'var(--bg-app)', borderRadius: 10, fontSize: 13 }}>
                    No users assigned to this role yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {roleUsers.map(u => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--action-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--status-resolved-text)', flexShrink: 0 }}>
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{u.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{u.email}</div>
                        </div>
                        <span style={{ fontSize: 11, background: 'var(--n-100)', borderRadius: 6, padding: '2px 8px', color: 'var(--text-secondary)', fontWeight: 600 }}>{u.role}</span>
                        <button onClick={() => removeUser(u.id)} style={{ background: 'var(--red-50)', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Health Check Tab ───────────────────────────────────────────────────────────
const HEALTH_SERVICES = [
  { key: 'database', label: 'Database',    icon: '🗄️' },
  { key: 'smtp',     label: 'SMTP (Email Out)', icon: '📤' },
  { key: 'imap',     label: 'IMAP (Email In)',  icon: '📥' },
  { key: 'disk',     label: 'Disk Space',  icon: '💾' },
  { key: 'memory',   label: 'Memory',      icon: '🧠' },
  { key: 'api',      label: 'API Server',  icon: '⚙️' },
];

function HealthTab() {
  const [results, setResults]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [ranAt, setRanAt]       = useState(null);

  async function runCheck() {
    setLoading(true);
    setResults(null);
    try {
      const data = await apiFetch('/maintenance/health');
      setResults(data);
      setRanAt(new Date());
    } catch (e) {
      setResults({ _error: e.message });
    }
    setLoading(false);
  }

  const allOk = results && !results._error && HEALTH_SERVICES.every(s => results[s.key]?.ok);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>System Health Check</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            Tests database, email (SMTP/IMAP), disk, memory and API connectivity.
            {ranAt && <span style={{ marginLeft: 10, color: 'var(--text-muted)' }}>Last run: {ranAt.toLocaleTimeString('en-IN')}</span>}
          </p>
        </div>
        <button className="btn btn-primary" onClick={runCheck} disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 160 }}>
          {loading ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Running…</> : '▶ Run Health Check'}
        </button>
      </div>

      {results?._error && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 10, padding: '14px 18px', fontSize: 13 }}>
          Failed to run health check: {results._error}
        </div>
      )}

      {results && !results._error && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '12px 18px', borderRadius: 10, background: allOk ? 'var(--status-resolved-bg)' : '#fee2e2', border: `1px solid ${allOk ? 'var(--status-resolved-text)' : '#fca5a5'}` }}>
            <span style={{ fontSize: 20 }}>{allOk ? '✅' : '⚠️'}</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: allOk ? 'var(--status-resolved-text)' : '#b91c1c' }}>
              {allOk ? 'All systems operational' : 'One or more services have issues'}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {HEALTH_SERVICES.map(s => {
              const r = results[s.key];
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: 'var(--bg-surface)', border: `1.5px solid ${r?.ok ? 'var(--status-resolved-text)' : '#fca5a5'}`, borderRadius: 12 }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{s.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{r?.detail}</div>
                  </div>
                  <span style={{ flexShrink: 0, padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: r?.ok ? 'var(--status-resolved-bg)' : '#fee2e2', color: r?.ok ? 'var(--status-resolved-text)' : '#b91c1c' }}>
                    {r?.ok ? '● OK' : '● FAIL'}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!results && !loading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
          <p style={{ fontSize: 14 }}>Click <strong>Run Health Check</strong> to test all services.</p>
        </div>
      )}
    </div>
  );
}

// ── Audit Trail Tab ────────────────────────────────────────────────────────────
function AuditTab() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showPurge, setShowPurge] = useState(false);
  const [purgeFrom, setPurgeFrom] = useState('');
  const [purgeTo, setPurgeTo]     = useState('');
  const [purging, setPurging]     = useState(false);
  const [purgeMsg, setPurgeMsg]   = useState(null);

  function loadRows() {
    setLoading(true); setError(null);
    apiFetch('/audit/logs')
      .then(data => { setRows(data || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }

  useEffect(() => { loadRows(); }, []);

  function handleExport() {
    const token = sessionStorage.getItem('kgreen_token');
    fetch('/api/audit/export', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  }

  async function handlePurge() {
    if (!purgeFrom || !purgeTo) return;
    if (!window.confirm(`Permanently delete all audit records from ${purgeFrom} to ${purgeTo}?\n\nThis cannot be undone.`)) return;
    setPurging(true); setPurgeMsg(null);
    try {
      const res = await apiFetch('/audit/purge', {
        method: 'DELETE',
        body: { from: purgeFrom, to: purgeTo },
      });
      setPurgeMsg({ type: 'success', text: `Deleted ${res.deleted} record(s) between ${purgeFrom} and ${purgeTo}.` });
      setShowPurge(false); setPurgeFrom(''); setPurgeTo('');
      loadRows();
    } catch (e) {
      setPurgeMsg({ type: 'error', text: e.message });
    }
    setPurging(false);
  }

  const fmt = iso => iso ? new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

  const ROLE_COLOR = { admin: '#7c3aed', manager: '#ea580c', agent: '#0B6623', customer: '#0369a1', customer_manager: '#be185d' };

  return (
    <div>
      {/* Toast */}
      {purgeMsg && (
        <div style={{ marginBottom: 16, padding: '12px 18px', borderRadius: 10,
          display: 'flex', alignItems: 'center', gap: 10,
          background: purgeMsg.type === 'success' ? 'var(--action-bg)' : 'var(--red-50)',
          border: `1px solid ${purgeMsg.type === 'success' ? 'var(--action-hover)' : '#fca5a5'}`,
          color: purgeMsg.type === 'success' ? 'var(--status-resolved-text)' : '#b91c1c',
          fontSize: 13, fontWeight: 600 }}>
          <span style={{ fontSize: 18 }}>{purgeMsg.type === 'success' ? '✅' : '❌'}</span>
          {purgeMsg.text}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Audit Trail</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            Login and logout activity. Showing last 200 entries.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-danger" onClick={() => { setShowPurge(v => !v); setPurgeMsg(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            🗑 Purge Logs
          </button>
          <button className="btn btn-primary" onClick={handleExport}
            style={{ background: '#0B6623', display: 'flex', alignItems: 'center', gap: 6 }}>
            ⬇ Download CSV
          </button>
        </div>
      </div>

      {/* Purge panel */}
      {showPurge && (
        <div style={{ marginBottom: 20, padding: '18px 20px', borderRadius: 12,
          background: 'var(--red-50)', border: '1.5px solid #fca5a5' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#b91c1c', marginBottom: 4 }}>
            🗑 Purge Audit Logs
          </div>
          <p style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 14 }}>
            Select a date range to permanently delete matching records. Download a CSV backup first if needed.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#7f1d1d', marginBottom: 4 }}>From</div>
              <input type="date" value={purgeFrom} onChange={e => setPurgeFrom(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #fca5a5',
                  fontSize: 13, background: '#fff', color: '#0f172a' }} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#7f1d1d', marginBottom: 4 }}>To</div>
              <input type="date" value={purgeTo} onChange={e => setPurgeTo(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #fca5a5',
                  fontSize: 13, background: '#fff', color: '#0f172a' }} />
            </div>
            <button onClick={handlePurge} disabled={!purgeFrom || !purgeTo || purging}
              className="btn btn-danger" style={{ minWidth: 130 }}>
              {purging ? 'Deleting…' : '⚠️ Confirm Purge'}
            </button>
            <button onClick={() => setShowPurge(false)} className="btn btn-ghost"
              style={{ fontSize: 13 }}>Cancel</button>
          </div>
        </div>
      )}


      {loading && <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>}
      {error && <div style={{ color: '#dc2626', fontSize: 13 }}>Error: {error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
          <p style={{ fontSize: 14 }}>No login activity yet. Entries appear after users log in.</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--n-50)' }}>
                {['User', 'Role', 'IP Address', 'Location', 'Login Time', 'Logout Time'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700,
                    color: 'var(--text-secondary)', textAlign: 'left',
                    textTransform: 'uppercase', letterSpacing: '.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.user_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.user_email}</div>
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                      background: `${ROLE_COLOR[r.user_role] || '#64748b'}18`,
                      color: ROLE_COLOR[r.user_role] || '#64748b' }}>
                      {r.user_role}
                    </span>
                  </td>
                  <td style={{ padding: '11px 14px', fontSize: 13, fontFamily: 'monospace' }}>{r.ip || '—'}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13 }}>
                    {r.city || r.country ? [r.city, r.country].filter(Boolean).join(', ') : '—'}
                  </td>
                  <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmt(r.login_at)}</td>
                  <td style={{ padding: '11px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {r.logout_at
                      ? <span style={{ color: 'var(--text-secondary)' }}>{fmt(r.logout_at)}</span>
                      : <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 11 }}>● Active</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Maintenance Page ──────────────────────────────────────────────────────
export default function Maintenance() {
  const [tab, setTab] = useState('backups');

  const TABS = [
    { id: 'backups', label: '🗄️ Backups & Restore' },
    { id: 'roles',   label: '🔐 Roles & Privileges' },
    { id: 'health',  label: '🩺 Health Check' },
    { id: 'audit',   label: '📋 Audit Trail' },
    { id: 'settings', label: '⚙️ Settings' },
  ];

  return (
    <div className="page">
      <div style={{ marginBottom: 24 }}>
        <h1 className="page-title">Maintenance</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>System administration — accessible to super admins only.</p>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 28 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '10px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
              color: tab === t.id ? 'var(--status-resolved-text)' : 'var(--text-secondary)',
              borderBottom: `3px solid ${tab === t.id ? 'var(--status-resolved-text)' : 'transparent'}`,
              marginBottom: -2, transition: 'all .15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'backups' && <BackupsTab />}
      {tab === 'roles'   && <RolesTab />}
      {tab === 'health'  && <HealthTab />}
      {tab === 'audit'   && <AuditTab />}
      {tab === 'settings' && <SettingsTab />}
    </div>
  );
}

const FEATURE_GROUPS = [
  { title: 'Modules', items: [
    ['chat.enabled', 'Live Chat'],
    ['knowledge.enabled', 'Knowledge Base & FAQ'],
    ['projects.enabled', 'Projects'],
    ['plan.enabled', "Today's Plan"],
    ['oof.enabled', 'OOF Manager'],
    ['subtickets.enabled', 'Sub-tickets'],
    ['reports.enabled', 'Reports & Analytics'],
  ]},
  { title: 'Ticket behaviour', items: [
    ['attachments.enabled', 'Screenshot attachments'],
    ['email_to_ticket.enabled', 'Email-to-ticket (inbound)'],
    ['customer_reopen.enabled', 'Customer can reopen tickets'],
    ['sla_stop.enabled', 'Allow agents to stop SLA clock'],
    ['sla_auto_resume.enabled', 'Auto-resume SLA on reply'],
    ['auto_assign.enabled', 'Auto-assign (round-robin)'],
    ['team_chat.enabled', 'Internal team chat on ticket'],
  ]},
  { title: 'Security & access', items: [
    ['require_mfa_staff', 'Require MFA for staff'],
    ['require_mfa_customers', 'Require MFA for customers'],
    ['self_registration.enabled', 'Allow customer self-registration'],
    ['self_reg_require_approval', 'Self-registration needs admin approval'],
    ['rbac.enforce', 'Enforce privilege RBAC'],
  ]},
];

function SettingsTab() {
  const [flags, setFlags] = React.useState(null);
  const [vals, setVals] = React.useState({});
  const [tenant, setTenant] = React.useState({ enabled: false, customer_id: '', customer_name: '' });
  const [stName, setStName] = React.useState('');
  const [stDomain, setStDomain] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  React.useEffect(() => {
    apiFetch('/settings')
      .then(s => { setFlags(s.flags || {}); setVals(s.values || {}); setTenant(s.tenant || {}); setStName((s.tenant && s.tenant.customer_name) || ''); })
      .catch(() => { setFlags({}); setVals({}); })
      .finally(() => setLoading(false));
  }, []);

  function toggle(key) { setFlags(f => ({ ...f, [key]: !f[key] })); }
  function setVal(k, v) { setVals(x => ({ ...x, [k]: v })); }

  async function save() {
    // Enabling single-tenant for the first time needs a client name
    if (flags['single_tenant.enabled'] && !tenant.customer_id && !stName.trim()) {
      setMsg({ type: 'error', text: 'Enter the client name to enable single-tenant mode.' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      const body = { flags, values: { ...vals } };
      if (flags['single_tenant.enabled'] && (stName.trim() || stDomain.trim())) {
        body.values.single_tenant_name = stName.trim();
        body.values.single_tenant_domain = stDomain.trim();
      }
      const s = await apiFetch('/settings', { method: 'PUT', body });
      setFlags(s.flags || {}); setVals(s.values || {}); setTenant(s.tenant || {});
      setStName(''); setStDomain('');
      setMsg({ type: 'success', text: 'Settings saved.' });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    setSaving(false);
    setTimeout(() => setMsg(null), 5000);
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><span className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 760 }}>
      {msg && (
        <div style={{ marginBottom: 18, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: msg.type === 'success' ? 'var(--action-bg)' : 'var(--red-50)',
          color: msg.type === 'success' ? 'var(--status-resolved-text)' : '#b91c1c',
          border: `1px solid ${msg.type === 'success' ? 'var(--action-hover)' : '#fca5a5'}` }}>
          {msg.type === 'success' ? '✅ ' : '❌ '}{msg.text}
        </div>
      )}

      <div className="card" style={{ padding: 22 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 2px' }}>Features</h3>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Turn modules and behaviours on or off for this organization.</p>

        {FEATURE_GROUPS.map(g => (
          <div key={g.title} style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 8 }}>{g.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '4px 20px' }}>
              {g.items.map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, padding: '5px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!flags[key]} onChange={() => toggle(key)} style={{ accentColor: '#0B6623', width: 15, height: 15 }} />
                  {label}
                </label>
              ))}
            </div>
          </div>
        ))}

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 10 }}>Values</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '12px 20px', fontSize: 13 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Reopen window (days)
              <input className="input" type="number" min={0} max={365} value={vals.reopen_window_days ?? 3} onChange={e => setVal('reopen_window_days', e.target.value)} style={{ width: 80 }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Max attachment size (MB)
              <input className="input" type="number" min={1} max={50} value={vals.attach_max_mb ?? 5} onChange={e => setVal('attach_max_mb', e.target.value)} style={{ width: 80 }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Max attachments / ticket
              <input className="input" type="number" min={1} max={20} value={vals.attach_max_count ?? 3} onChange={e => setVal('attach_max_count', e.target.value)} style={{ width: 80 }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Removed-user tickets
              <select className="input" value={vals.removed_user_ticket_action || 'admin_queue'} onChange={e => setVal('removed_user_ticket_action', e.target.value)} style={{ maxWidth: 190 }}>
                <option value="admin_queue">Send to admin queue</option>
                <option value="auto_reassign">Auto-reassign</option>
              </select>
            </label>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 4 }}>Ticket allocation</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>
            How <b>client-raised</b> tickets are auto-assigned. Staff-created tickets always stay with their creator; OOF / inactive agents are skipped; if none is eligible the ticket goes to the admin queue.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            {[
              ['skill', '🎯', 'Skill driven', 'By product skill & tier — favour experienced L1, then least-loaded.'],
              ['round_robin', '🔄', 'Round Robin', 'Even rotation across all active agents — next ticket to whoever was assigned one longest ago.'],
              ['admin', '🧑‍💼', 'Admin driven', 'No auto-assignment — every client ticket lands in the pool (unassigned) for an admin to assign manually.'],
            ].map(([val, ic, title, desc]) => {
              const on = (vals.ticket_allocation || 'skill') === val;
              return (
                <div key={val} onClick={() => setVal('ticket_allocation', val)} style={{
                  border: `2px solid ${on ? '#0B6623' : 'var(--border-subtle)'}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
                  background: on ? 'var(--action-bg)' : 'var(--bg-surface)', transition: 'all .15s',
                }}>
                  <div style={{ fontSize: 22 }}>{ic}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, margin: '4px 0 2px', color: on ? '#0B6623' : 'var(--text-primary)' }}>{title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 8 }}>Deployment</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!flags['single_tenant.enabled']} onChange={() => toggle('single_tenant.enabled')} style={{ accentColor: '#0B6623', width: 15, height: 15 }} />
            Single-tenant mode (one client, many products)
          </label>
          {flags['single_tenant.enabled'] && (
            <div style={{ margin: '10px 0 0 25px', padding: 12, background: 'var(--bg-app)', border: '1px solid var(--border-subtle)', borderRadius: 8, maxWidth: 460 }}>
              <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
                {tenant.customer_id ? 'The client this instance serves. Edit the name below and Save.' : 'Enter the client this instance serves. A customer record is created and used everywhere automatically.'}
              </p>
              <div style={{ display: 'grid', gap: 8 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Client name</label>
                  <input className="input" placeholder="Client name (e.g. Acme Bank Ltd)" value={stName} onChange={e => setStName(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{tenant.customer_id ? 'Add another domain (optional)' : 'Email domain'}</label>
                  <input className="input" placeholder="acme.com" value={stDomain} onChange={e => setStDomain(e.target.value.replace(/^@/, ''))} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ background: '#0B6623' }}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
